import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IDateRangePreset, TDateRangePresetId } from "../interfaces";
import { DEMO_ACCOUNTS, getPresets, LOCAL_STORAGE_KEYS } from "../shared/constants";
import dayjs from "dayjs";
import { useAuth } from "../hooks";
import { DateRangeContext } from "./DateRangeContextDefinition";

export const DateRangeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // A range passed in the URL (e.g. list → detail page) wins over the one saved in localStorage,
  // so a page opened from another one shows the same period.
  const urlFrom = searchParams.get("from");
  const urlTo = searchParams.get("to");
  const hasUrlRange =
    !!urlFrom && !!urlTo &&
    dayjs(urlFrom, "YYYY-MM-DD", true).isValid() &&
    dayjs(urlTo, "YYYY-MM-DD", true).isValid();

  const [selectedPreset, setSelectedPreset] = useState<IDateRangePreset | undefined>(() => {
    if (hasUrlRange) {
      const urlPresetId = searchParams.get("preset") as TDateRangePresetId | null;
      return urlPresetId ? getPresets(t).find((p) => p.id === urlPresetId) : undefined;
    }

    try {
      const storedPreset = localStorage.getItem(
        LOCAL_STORAGE_KEYS.DATA_RANGE_PRESET
      ) as TDateRangePresetId | null;
      if (storedPreset) {
        const found = getPresets(t).find((p) => p.id === storedPreset);
        if (found) return found;
      }
    } catch { /* empty */ }

    const demoAccounts = DEMO_ACCOUNTS.includes(String(user?.schema));
    const presetType = demoAccounts ? "lastMonth" : "last30Days";

    const presetId = searchParams.get("preset") as TDateRangePresetId;
    if (presetId) {
      return getPresets(t).find((p) => p.id === presetId);
    }

    return getPresets(t).find((p) => p.id === presetType);
  });

  const [dateRange, setDateRangeState] = useState<{ startDate: Date; endDate: Date }>(() => {
    // If a preset is active (and not custom), recalculate from it so relative ranges stay current.
    if (selectedPreset && selectedPreset.id !== "custom") {
      const [s, e] = selectedPreset.getRange();
      return { startDate: s as Date, endDate: e as Date };
    }

    // Parse as local midnight; new Date("YYYY-MM-DD") would parse as UTC midnight.
    if (hasUrlRange) {
      return { startDate: dayjs(urlFrom, "YYYY-MM-DD").toDate(), endDate: dayjs(urlTo, "YYYY-MM-DD").toDate() };
    }

    try {
      const lsFrom = localStorage.getItem(LOCAL_STORAGE_KEYS.DATA_RANGE_FROM);
      const lsTo = localStorage.getItem(LOCAL_STORAGE_KEYS.DATA_RANGE_TO);
      if (lsFrom && lsTo) {
        const fromValid = dayjs(lsFrom, "YYYY-MM-DD", true).isValid();
        const toValid = dayjs(lsTo, "YYYY-MM-DD", true).isValid();
        if (fromValid && toValid) {
          return { startDate: dayjs(lsFrom, "YYYY-MM-DD").toDate(), endDate: dayjs(lsTo, "YYYY-MM-DD").toDate() };
        }
      }
    } catch { /* empty */ }

    return { startDate: new Date(), endDate: new Date() };
  });

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set("from", dayjs(dateRange.startDate).format("YYYY-MM-DD"));
    params.set("to", dayjs(dateRange.endDate).format("YYYY-MM-DD"));
    if (selectedPreset) {
      params.set("preset", selectedPreset.id);
    } else {
      params.delete("preset");
    }
    setSearchParams(params, { replace: true });

    try {
      localStorage.setItem(LOCAL_STORAGE_KEYS.DATA_RANGE_FROM, dayjs(dateRange.startDate).format("YYYY-MM-DD"));
      localStorage.setItem(LOCAL_STORAGE_KEYS.DATA_RANGE_TO, dayjs(dateRange.endDate).format("YYYY-MM-DD"));
      if (selectedPreset) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.DATA_RANGE_PRESET, selectedPreset.id);
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.DATA_RANGE_PRESET);
      }
    } catch { /* empty */ }
  }, [dateRange, searchParams, selectedPreset, setSearchParams]);

  const setDateRange = (startDate: Date, endDate: Date) => {
    setDateRangeState({ startDate, endDate });
    setSelectedPreset(undefined);
  };

  const value = useMemo(
    () => ({ dateRange, selectedPreset, setDateRange, setSelectedPreset }),
    [dateRange, selectedPreset]
  );

  return (
    <DateRangeContext.Provider value={value}>
      {children}
    </DateRangeContext.Provider>
  );
};
